import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { OfferShiftModal } from './OfferShiftModal.jsx';

// Format a swap-request shift line (used only here).
function fmtSwapShift(r) {
  const d = r.shift_date || '';
  const s = (r.start_time || '').slice(0, 5);
  const e = (r.end_time || '').slice(0, 5);
  return `${d}  ${s}–${e}${r.shift_role ? ` · ${r.shift_role}` : ''}${r.location_name ? ` · ${r.location_name}` : ''}`;
}

export function ShiftSwapsView({ currentUser, isManager }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [data, setData] = useState({ outgoing: [], incoming: [], open: [] });
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);
  const [showOffer, setShowOffer] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api('GET', '/api/me/swaps', null, headers);
      setData({ outgoing: r.outgoing || [], incoming: r.incoming || [], open: r.open || [] });
      if (isManager) {
        const q = await api('GET', '/api/admin/swaps?status=accepted_pending', null, headers);
        setQueue(q.requests || []);
      }
    } catch (e) { setErr(e.message || String(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function act(path, body, label) {
    setBusy(path);
    try {
      await api('POST', path, body || {}, headers);
      await load();
    } catch (e) { alert(`${label} failed: ${e.message || e}`); }
    finally { setBusy(null); }
  }

  if (loading) return <div className="card text-sm text-gray-500">Loading…</div>;

  return (
    <div className="space-y-4">
      {err && <div className="card" style={{ borderLeft: '4px solid #c62828' }}><div className="text-xs text-red-700">{err}</div></div>}

      {/* Offer one of my shifts */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="text-base font-semibold">Shift swaps</div>
            <div className="text-xs text-gray-500">Offer a shift to a coworker, or pick up an open shift.</div>
          </div>
          <button onClick={() => setShowOffer(true)} className="btn-primary">+ Offer a shift</button>
        </div>
      </div>

      {/* Manager queue */}
      {isManager && queue.length > 0 && (
        <div className="card" style={{ borderLeft: '4px solid #ef6c00' }}>
          <div className="text-base font-semibold mb-2">Awaiting your approval ({queue.length})</div>
          {queue.map((r) => (
            <div key={r.id} style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8, marginTop: 8 }}>
              <div className="text-sm font-semibold">{fmtSwapShift(r)}</div>
              <div className="text-xs text-gray-700" style={{ marginTop: 2 }}>
                <strong>{r.requester_name}</strong> ({r.requester_role || 'staff'})
                <span style={{ margin: '0 6px' }}>→</span>
                <strong>{r.acceptor_name}</strong> ({r.acceptor_role || 'staff'})
              </div>
              {r.reason && <div className="text-xs" style={{ color: '#757575', marginTop: 2 }}>“{r.reason}”</div>}
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button disabled={busy} className="btn-primary"
                  onClick={() => act(`/api/admin/swaps/${r.id}/approve`, null, 'Approve')}>
                  Approve
                </button>
                <button disabled={busy} className="btn-secondary"
                  onClick={() => {
                    const note = prompt('Reason for denial?');
                    if (note === null) return;
                    act(`/api/admin/swaps/${r.id}/deny`, { review_note: note }, 'Deny');
                  }}>
                  Deny
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Direct incoming */}
      {data.incoming.length > 0 && (
        <div className="card">
          <div className="text-base font-semibold mb-2">Offered to you ({data.incoming.length})</div>
          {data.incoming.map((r) => (
            <div key={r.id} style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8, marginTop: 8 }}>
              <div className="text-sm font-semibold">{fmtSwapShift(r)}</div>
              <div className="text-xs text-gray-700">From {r.requester_name}</div>
              {/* A TRADE has to say what it costs. Accepting one hands over a
                  shift of your own, and a button that does that without
                  showing you which shift is a button nobody should press. */}
              {r.counter_shift_id && r.counter_date && (
                <div style={{ marginTop: 6, padding: '6px 9px', borderRadius: 6,
                              background: '#f5e9d6', color: '#8a5a1a', fontSize: 12 }}>
                  <b>Trade</b> — you give up your {r.counter_date}{' '}
                  {String(r.counter_start || '').slice(0, 5)}–{String(r.counter_end || '').slice(0, 5)}
                  {r.counter_location_name ? ` · ${r.counter_location_name}` : ''}
                </div>
              )}
              {r.reason && <div className="text-xs" style={{ color: '#757575', marginTop: 2 }}>“{r.reason}”</div>}
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button disabled={busy} className="btn-primary"
                  onClick={() => act(`/api/swaps/${r.id}/accept`, null,
                    r.counter_shift_id ? 'Accept trade' : 'Accept')}>
                  {r.counter_shift_id ? 'Accept trade' : 'Accept'}
                </button>
                <button disabled={busy} className="btn-secondary"
                  onClick={() => act(`/api/swaps/${r.id}/decline`, null, 'Decline')}>
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Open offers I can pick up */}
      {data.open.length > 0 && (
        <div className="card">
          <div className="text-base font-semibold mb-2">Open shifts ({data.open.length})</div>
          {data.open.map((r) => (
            <div key={r.id} style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8, marginTop: 8 }}>
              <div className="text-sm font-semibold">{fmtSwapShift(r)}</div>
              <div className="text-xs text-gray-700">From {r.requester_name}</div>
              {r.reason && <div className="text-xs" style={{ color: '#757575', marginTop: 2 }}>“{r.reason}”</div>}
              <div style={{ marginTop: 8 }}>
                <button disabled={busy} className="btn-primary"
                  onClick={() => act(`/api/swaps/${r.id}/accept`, null, 'Pick up')}>
                  Pick up
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* My outgoing */}
      <div className="card">
        <div className="text-base font-semibold mb-2">Your offers</div>
        {data.outgoing.length === 0 ? (
          <div className="text-sm text-gray-500">No offers yet.</div>
        ) : (
          data.outgoing.map((r) => (
            <div key={r.id} style={{ borderTop: '1px solid #f0f0f0', paddingTop: 8, marginTop: 8 }}>
              <div className="text-sm font-semibold">{fmtSwapShift(r)}</div>
              <div className="text-xs text-gray-700">
                {r.target_user_id ? `To ${r.target_name}` : 'Open to anyone at the location'}
                <span style={{ marginLeft: 8, fontWeight: 600,
                                color: r.status === 'approved' ? '#2e7d32' :
                                       r.status === 'denied' || r.status === 'declined' || r.status === 'cancelled' ? '#c62828' :
                                       '#ef6c00' }}>
                  · {r.status.replace('_', ' ')}
                </span>
                {r.acceptor_name && <span> · {r.acceptor_name}</span>}
              </div>
              {(r.status === 'pending' || r.status === 'accepted_pending') && (
                <div style={{ marginTop: 8 }}>
                  <button disabled={busy} className="btn-secondary"
                    onClick={() => { if (confirm('Cancel this offer?')) act(`/api/swaps/${r.id}/cancel`, null, 'Cancel'); }}>
                    Cancel offer
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {showOffer && (
        <OfferShiftModal currentUser={currentUser} onClose={() => setShowOffer(false)} onCreated={() => { setShowOffer(false); load(); }} />
      )}
    </div>
  );
}
